import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

interface ToastProps {
  message: string;
  onClose: () => void;
  duration?: number;
  /** true면 자동 닫힘 없이 확인 버튼 클릭 시에만 닫힘 */
  confirmOnly?: boolean;
  confirmLabel?: string;
  /** 작은 글자로 표시할 보조 메시지 (예: 환불 계산식) */
  messageDetail?: string;
}

export const Toast: React.FC<ToastProps> = ({ message, onClose, duration = 1000, confirmOnly = false, confirmLabel = '확인', messageDetail }) => {
  useEffect(() => {
    if (confirmOnly) return;
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose, confirmOnly]);

  return (
    <ToastOverlay onClick={confirmOnly ? undefined : onClose} $confirmOnly={confirmOnly}>
      <ToastBox $confirmOnly={confirmOnly} onClick={confirmOnly ? (e) => e.stopPropagation() : undefined}>
        <div>
          <ToastMessage>{message}</ToastMessage>
          {messageDetail && <ToastMessageDetail>{messageDetail}</ToastMessageDetail>}
        </div>
        {confirmOnly && (
          <ToastConfirmButton onClick={onClose}>{confirmLabel}</ToastConfirmButton>
        )}
      </ToastBox>
    </ToastOverlay>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ToastOverlay = styled.div<{ $confirmOnly?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  animation: ${fadeIn} 0.2s ease-in-out;
  cursor: ${props => (props.$confirmOnly ? 'default' : 'pointer')};
`;

const ToastBox = styled.div<{ $confirmOnly?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: ${props => (props.$confirmOnly ? '20px 32px 24px' : '20px 32px')};
  border-radius: 12px;
  background: #ffffff !important;
  color: #000000 !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.3s ease-out;
`;

const ToastMessage = styled.div`
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  white-space: pre-line;
  color: inherit;
`;

const ToastMessageDetail = styled.div`
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  white-space: pre-line;
  color: inherit;
  opacity: 0.85;
  margin-top: 8px;
`;

const ToastConfirmButton = styled.button`
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  background: var(--color-accent-strong, #22c55e);
  color: white;
  cursor: pointer;
  min-width: 80px;
  &:hover {
    opacity: 0.9;
  }
`;

